import { useEffect, useState } from 'react';
import { apiFetch, apiWrite } from '../../lib/api-client.js';

interface DetailResponse {
  error?: string;
  employee?: Record<string, unknown> | null;
  edit_log?: unknown[];
  assets?: unknown[];
}

interface PatchResponse {
  error?: string;
}

const BASIC_EDIT_FIELDS = [
  'first_name',
  'last_name',
  'nickname',
  'middle_name',
  'phone',
  'email',
  'status',
  'staffing_type',
  'hire_date',
  'emergency_contact_name',
  'emergency_contact_phone',
  'emergency_contact_relationship',
];

const COMPENSATION_FIELDS = [
  'pay_rate',
  'rate_type',
  'pay_rate_note',
  'per_diem_eligible',
  'per_diem_rate',
];

const HR_SENSITIVE_FIELDS = [
  'dob',
  'dl_number',
  'address_line1',
  'address_line2',
  'address_city',
  'address_state',
  'address_zip',
  'shirt_size',
  'pants_size',
  'jacket_size',
  'fedex_experience',
  'reports_to_email',
];

function pickFields(form: Record<string, unknown>, allowedFields: string[]) {
  return allowedFields.reduce((payload: Record<string, unknown>, field: string) => {
    if (form[field] !== undefined) payload[field] = form[field];
    return payload;
  }, {});
}

export function useDirectoryDetail(employeeId: string | number | null) {
  const [employee, setEmployee] = useState<Record<string, unknown> | null>(null);
  const [editLog, setEditLog] = useState<unknown[]>([]);
  const [assets, setAssets] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  async function loadDetail(targetEmployeeId = employeeId) {
    if (!targetEmployeeId) {
      setEmployee(null);
      setEditLog([]);
      setAssets([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<DetailResponse>(`/api/directory/${targetEmployeeId}`);

      if (data.error) {
        setError(data.error);
        setEmployee(null);
        setEditLog([]);
        setAssets([]);
        return;
      }

      setEmployee(data.employee || null);
      setEditLog(data.edit_log || []);
      setAssets(data.assets || []);
    } catch (err) {
      setError((err as Error).message || 'Directory detail load failed');
      setEmployee(null);
      setEditLog([]);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let active = true;

    async function run() {
      if (!active) return;
      await loadDetail(employeeId);
    }

    run();
    return () => {
      active = false;
    };
  }, [employeeId]);

  async function patchDetail(payload: Record<string, unknown>) {
    if (!employeeId) {
      return { ok: false, error: 'No employee selected' };
    }

    if (Object.keys(payload).length === 0) {
      return { ok: true, skipped: true };
    }

    setSaving(true);
    setError(null);

    try {
      const result = await apiWrite<PatchResponse>(`/api/directory/${employeeId}`, 'PATCH', payload);
      if (!result.ok) {
        const message = result.data?.error || 'Directory save failed';
        setError(message);
        return { ok: false, error: message };
      }

      await loadDetail(employeeId);
      return { ok: true };
    } catch (err) {
      const message = (err as Error).message || 'Directory save failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setSaving(false);
    }
  }

  async function saveBasicDetail(form: Record<string, unknown>) {
    return patchDetail(pickFields(form, BASIC_EDIT_FIELDS));
  }

  async function saveCompensationDetail(form: Record<string, unknown>) {
    const payload = pickFields(form, COMPENSATION_FIELDS);
    if (Object.keys(payload).length === 0) {
      return { ok: true, skipped: true };
    }

    return patchDetail({
      ...payload,
      confirmation_code: 'GG42',
    });
  }

  async function saveHrSensitiveDetail(form: Record<string, unknown>) {
    return patchDetail(pickFields(form, HR_SENSITIVE_FIELDS));
  }

  async function reportAssetIssue({ asset_id, status, notes }: { asset_id: string; status: string; notes: string }) {
    setReporting(true);
    setError(null);

    try {
      const result = await apiWrite('/api/directory/asset/report', 'POST', {
        asset_id,
        status,
        notes,
      });

      if (!result.ok) {
        const message = (result.data as PatchResponse)?.error || 'Asset report failed';
        setError(message);
        return { ok: false, error: message };
      }

      await loadDetail(employeeId);
      return { ok: true };
    } catch (err) {
      const message = (err as Error).message || 'Asset report failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setReporting(false);
    }
  }

  async function promoteEmployee(payload: Record<string, unknown>) {
    if (!employeeId) {
      return { ok: false, error: 'No employee selected' };
    }

    setRoleSubmitting(true);
    setError(null);

    try {
      const result = await apiWrite<PatchResponse>(`/api/directory/${employeeId}/role-change`, 'POST', payload);
      if (!result.ok) {
        const message = result.data?.error || 'Role change failed';
        setError(message);
        return { ok: false, error: message };
      }

      await loadDetail(employeeId);
      return { ok: true, data: result.data };
    } catch (err) {
      const message = (err as Error).message || 'Role change failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setRoleSubmitting(false);
    }
  }

  async function demoteEmployee() {
    return promoteEmployee({ action: 'demote' });
  }

  async function uploadPhoto(photoData: string) {
    if (!employeeId) {
      return { ok: false, error: 'No employee selected' };
    }

    setSaving(true);
    setError(null);

    try {
      const result = await apiWrite<{ success?: boolean; photo_url?: string; error?: string }>(
        '/api/directory/photo',
        'POST',
        { employee_id: employeeId, photo_data: photoData },
      );

      if (!result.ok || result.data.error) {
        const message = result.data.error || 'Photo upload failed';
        setError(message);
        return { ok: false, error: message };
      }

      await loadDetail(employeeId);
      return { ok: true, photo_url: result.data.photo_url };
    } catch (err) {
      const message = (err as Error).message || 'Photo upload failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setSaving(false);
    }
  }

  async function sendRegistrationInvite(email: string) {
    setSaving(true);
    setError(null);

    try {
      const result = await apiWrite<{ success?: boolean; invite_link?: string; expires_at?: string; error?: string }>(
        '/api/registration/invite',
        'POST',
        { email },
      );

      if (!result.ok || result.data.error) {
        const message = result.data.error || 'Invite send failed';
        setError(message);
        return { ok: false, error: message };
      }

      return { ok: true, invite_link: result.data.invite_link, expires_at: result.data.expires_at };
    } catch (err) {
      const message = (err as Error).message || 'Invite send failed';
      setError(message);
      return { ok: false, error: message };
    } finally {
      setSaving(false);
    }
  }

  return {
    employee,
    editLog,
    assets,
    loading,
    error,
    saving,
    reporting,
    roleSubmitting,
    saveBasicDetail,
    saveCompensationDetail,
    saveHrSensitiveDetail,
    reportAssetIssue,
    promoteEmployee,
    demoteEmployee,
    uploadPhoto,
    sendRegistrationInvite,
  };
}
